// MarketingDetailDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';
import { useConfirm } from '../../../components/common/ConfirmDialog';

export const MarketingDetailDialog = ({ open, onOpenChange, campaign, accountId, onSuccess, contactEntries = [], canEditAccount, usersMap = {} }) => {
  const { user: currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  
  useEffect(() => {
    if (campaign && contactEntries && accountId) {
      // Find contact entry from the campaign's contact_entry_mappings
      const mappedContactId = campaign.contact_entry_mappings?.[accountId];
      setSelectedContactId(mappedContactId || '');
    }
  }, [campaign, contactEntries, accountId]);
  
  if (!campaign) return null;

  const canEdit = canEditAccount !== undefined ? canEditAccount : true; // Default to true if not provided

  // Filter contacts based on campaign type
  const filteredContacts = contactEntries.filter(entry => {
    if (campaign.campaign_type === 'email') {
      return entry.contact_type === 'email';
    } else if (campaign.campaign_type === 'telephone') {
      return entry.contact_type === 'phone';
    } else if (campaign.campaign_type === 'direct_mailer') {
      return entry.contact_type === 'address';
    }
    return true;
  });

  const selectedContact = contactEntries.find(e => e.id === selectedContactId);

  const getContactDisplay = (contact) => {
    if (!contact) return null;
    
    let value = '';
    let type = '';
    
    if (contact.contact_type === 'email') {
      value = contact.email_address;
      type = `Email${contact.email_type ? ' - ' + contact.email_type : ''}`;
    } else if (contact.contact_type === 'phone') {
      value = contact.phone_number;
      type = `Phone${contact.phone_type ? ' - ' + contact.phone_type : ''}`;
    } else if (contact.contact_type === 'address') {
      value = `${contact.street}, ${contact.city}`;
      type = `Address${contact.address_type ? ' - ' + contact.address_type : ''}`;
    }
    
    return { value, type };
  };

  const handleSaveContactDetail = async () => {
    if (!campaign?.id || !accountId) return;
    setSaving(true);
    try {
      // Update the campaign's contact entry mappings
      const updatedMappings = { ...(campaign.contact_entry_mappings || {}) };
      if (selectedContactId) {
        updatedMappings[accountId] = selectedContactId;
      } else {
        delete updatedMappings[accountId];
      }
      
      await marketingAPI.update(campaign.id, {
        contact_entry_mappings: updatedMappings,
      });
      
      toast.success('Contact detail updated');
      setIsEditing(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to update contact detail:', error);
      toast.error('Failed to update contact detail');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromCampaign = async () => {
    if (!campaign?.id || !accountId) return;
    if (!await confirm({ title: 'Are you sure you want to remove this account from the campaign?', destructive: true })) return;
    
    setSaving(true);
    try {
      // Remove accountId from campaign's entity list
      const updatedEntityIds = (campaign.crm_entity_ids || []).filter(id => id !== accountId);
      
      await marketingAPI.update(campaign.id, {
        crm_entity_ids: updatedEntityIds,
      });
      
      toast.success('Removed from campaign');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to remove from campaign:', error);
      toast.error('Failed to remove from campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="marketing-detail-dialog">
        <DialogHeader>
          <DialogTitle>Campaign Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="p-4 bg-muted/30 rounded">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold">{campaign.name}</h3>
              <Badge variant={(campaign.is_active || campaign.status === 'active') ? 'default' : 'outline'} className="ml-3">
                {(campaign.is_active || campaign.status === 'active') ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Type:</span>
                <span className="capitalize">{campaign.campaign_type?.replace('_', ' ')}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Frequency:</span>
                <span className="capitalize">{campaign.frequency?.replace('_', ' ') || 'Not set'}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Total Accounts:</span>
                <span>{campaign.entity_count || campaign.crm_entity_ids?.length || 0}</span>
              </div>
              
              {selectedContact && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Contact Detail:</span>
                  </div>
                  <div className="ml-6 text-sm">
                    <p className="font-medium">{getContactDisplay(selectedContact)?.value}</p>
                    <p className="text-xs text-muted-foreground">({getContactDisplay(selectedContact)?.type})</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {campaign.notes && (
            <div className="space-y-1 pt-2">
              <Label className="text-sm font-medium">Notes</Label>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{campaign.notes}</p>
            </div>
          )}
          
          {isEditing && (
            <div className="space-y-2 pt-2">
              <Label>Change Contact Detail</Label>
              <Select 
                value={selectedContactId || 'none'} 
                onValueChange={(val) => setSelectedContactId(val === 'none' ? '' : val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact detail" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None selected</SelectItem>
                  {filteredContacts.map(entry => (
                    <SelectItem key={entry.id} value={entry.id}>
                      {entry.contact_type === 'email' && `${entry.email_address} (Email${entry.email_type ? ' - ' + entry.email_type : ''})`}
                      {entry.contact_type === 'phone' && `${entry.phone_number} (Phone${entry.phone_type ? ' - ' + entry.phone_type : ''})`}
                      {entry.contact_type === 'address' && `${entry.street}, ${entry.city} (Address${entry.address_type ? ' - ' + entry.address_type : ''})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Created Info */}
          {!isEditing && campaign.created_at && (
            <div className="pt-3 border-t text-xs text-muted-foreground">
              Created: {new Date(campaign.created_at).toLocaleDateString()} 
              {campaign.created_by && usersMap[campaign.created_by] && (
                <> by {usersMap[campaign.created_by].name}</>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {isEditing ? (
            <div className="flex justify-between w-full">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRemoveFromCampaign} 
                disabled={saving}
                data-testid="remove-from-campaign-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveContactDetail} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between w-full">
              {canEdit && (
                <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)} data-testid="edit-campaign-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

